import {
	AlertTriangle,
	Award,
	CalendarClock,
	Edit,
	Users,
	X,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { CustomerSuccess } from "../types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

interface CustomerSuccessSectionProps {
	data?: CustomerSuccess;
	readOnly?: boolean;
	onUpdate?: (data: CustomerSuccess) => void;
}

const tierColors: Record<number, { bg: string; text: string; label: string }> =
	{
		1: {
			bg: "bg-(--color-success-50)",
			text: "text-(--color-success-700)",
			label: "Tier 1",
		},
		2: {
			bg: "bg-(--color-info-50)",
			text: "text-(--color-info-700)",
			label: "Tier 2",
		},
		3: {
			bg: "bg-(--color-warning-50)",
			text: "text-(--color-warning-700)",
			label: "Tier 3",
		},
	};

const MEETING_FREQUENCIES = [
	"Every two weeks",
	"Monthly",
	"Quarterly",
	"As needed",
] as const;

const CustomerSuccessSection: React.FC<CustomerSuccessSectionProps> = ({
	data,
	readOnly = false,
	onUpdate,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editData, setEditData] = useState<CustomerSuccess>({});

	if (!data && readOnly) return null;

	const hasSomeData =
		data?.kam ||
		data?.tier ||
		data?.meeting_frequency ||
		data?.tasks_progress != null;

	if (!hasSomeData && readOnly) return null;

	const tier = data?.tier ? tierColors[data.tier] : null;
	const progress = data?.tasks_progress ?? 0;

	const startEditing = () => {
		setEditData({ ...data } as CustomerSuccess);
		setIsEditing(true);
	};

	const handleSave = () => {
		onUpdate?.(editData);
		setIsEditing(false);
	};

	const handleCancel = () => {
		setIsEditing(false);
		setEditData({});
	};

	if (isEditing && !readOnly) {
		return (
			<Card className="mb-4 p-6">
				<div className="mb-5 flex items-center justify-between">
					<h3 className="flex items-center gap-2 font-semibold text-(--color-foreground) text-lg">
						<Users size={18} className="text-(--color-primary)" />
						Customer Success
					</h3>
					<div className="flex gap-2">
						<Button size="sm" variant="outline" onClick={handleCancel}>
							<X size={14} />
							Cancel
						</Button>
						<Button size="sm" onClick={handleSave}>
							Save
						</Button>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Key Account Manager
						</label>
						<Input
							value={editData.kam ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({ ...prev, kam: e.target.value }))
							}
							placeholder="e.g. John Smith"
						/>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Tier
						</label>
						<Select
							value={editData.tier?.toString() ?? ""}
							onValueChange={(v) =>
								setEditData((prev) => ({
									...prev,
									tier: v ? (Number(v) as 1 | 2 | 3) : undefined,
								}))
							}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select tier" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="1">Tier 1</SelectItem>
								<SelectItem value="2">Tier 2</SelectItem>
								<SelectItem value="3">Tier 3</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Meeting Frequency
						</label>
						<Select
							value={editData.meeting_frequency ?? ""}
							onValueChange={(v) =>
								setEditData((prev) => ({
									...prev,
									meeting_frequency: v as CustomerSuccess["meeting_frequency"],
								}))
							}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select frequency" />
							</SelectTrigger>
							<SelectContent>
								{MEETING_FREQUENCIES.map((freq) => (
									<SelectItem key={freq} value={freq}>
										{freq}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Tasks Progress (%)
						</label>
						<Input
							type="number"
							min={0}
							max={100}
							value={editData.tasks_progress ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									tasks_progress: e.target.value
										? Number(e.target.value)
										: undefined,
								}))
							}
							placeholder="0-100"
						/>
					</div>

					<div>
						<label className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Missing Items Count
						</label>
						<Input
							type="number"
							min={0}
							value={editData.missing_items_count ?? ""}
							onChange={(e) =>
								setEditData((prev) => ({
									...prev,
									missing_items_count: e.target.value
										? Number(e.target.value)
										: undefined,
								}))
							}
							placeholder="0"
						/>
					</div>
				</div>
			</Card>
		);
	}

	return (
		<Card className="mb-4 p-6">
			<div className="mb-5 flex items-center justify-between">
				<h3 className="flex items-center gap-2 font-semibold text-(--color-foreground) text-lg">
					<Users size={18} className="text-(--color-primary)" />
					Customer Success
				</h3>
				<div className="flex items-center gap-2">
					{tier && (
						<span
							className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold text-xs ${tier.bg} ${tier.text}`}
						>
							<Award size={12} />
							{tier.label}
						</span>
					)}
					{!readOnly && onUpdate && (
						<Button size="sm" variant="ghost" onClick={startEditing}>
							<Edit size={14} />
						</Button>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{data?.kam && (
					<div className="rounded-(--radius-sm) bg-(--color-muted) p-3">
						<span className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Key Account Manager
						</span>
						<span className="font-semibold text-(--color-foreground) text-sm">
							{data.kam}
						</span>
					</div>
				)}

				{data?.meeting_frequency && (
					<div className="rounded-(--radius-sm) bg-(--color-muted) p-3">
						<span className="mb-1 block font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Meeting Frequency
						</span>
						<span className="flex items-center gap-1.5 font-semibold text-(--color-foreground) text-sm">
							<CalendarClock size={14} className="text-(--color-primary)" />
							{data.meeting_frequency}
						</span>
					</div>
				)}

				{data?.missing_items_count != null && data.missing_items_count > 0 && (
					<div className="rounded-(--radius-sm) bg-(--color-warning-50) p-3">
						<span className="mb-1 block font-medium text-(--color-warning-700) text-xs uppercase tracking-wider">
							Missing Items
						</span>
						<span className="flex items-center gap-1.5 font-semibold text-(--color-warning-700) text-sm">
							<AlertTriangle size={14} />
							{data.missing_items_count} item
							{data.missing_items_count > 1 ? "s" : ""}
						</span>
					</div>
				)}
			</div>

			{data?.tasks_progress != null && (
				<div className="mt-4 rounded-(--radius-sm) border border-(--color-border) p-3">
					<div className="mb-2 flex items-center justify-between">
						<span className="font-medium text-(--color-muted-foreground) text-xs uppercase tracking-wider">
							Task Progress
						</span>
						<Badge
							variant={
								progress >= 80
									? "success"
									: progress >= 50
										? "warning"
										: "neutral"
							}
						>
							{progress}%
						</Badge>
					</div>
					<Progress value={progress} className="h-2" />
				</div>
			)}
		</Card>
	);
};

export default CustomerSuccessSection;
